import { injectable, inject } from 'inversify';
import type { ICasePersistent, ICaseState } from './CasePersistance/CasePersistenceHub';
import { TYPES } from '@/engine/ioc';
import type { ViewsService } from './Views.service';
import { MeasurementKind } from './interfaces';
import { SceneItemKind } from './interfaces';
import { MeasurementViewModel } from './models/MeasurementViewModel';
import { runInAction } from 'mobx';
import { defaultMeasurementModelParams } from './View.factory';
import type { SliceType } from './modules/images/interfaces';
import type { ImagesMode } from './modules/images/interfaces';

export interface viewData {
  viewDescriptor: string;
  measurements: Array<MeasurementData>;
}

export interface MeasurementData {
  descriptor: string;
  label: string;
  kind: SceneItemKind;
  phaseId: string | undefined;
  syncSliceDescriptor: SliceType;
  imagesMode: ImagesMode;
  zoomFactor: number;
  attachments: Array<string>;
  details?: unknown;
  attachmentData?: unknown;
}


export type StateMetadata = Array<viewData>;

@injectable()
export class DiameterMeasurementsResolver implements ICasePersistent {
  private readonly _viewsService: ViewsService;

  public constructor(@inject(TYPES.ViewsService) viewsService: ViewsService) {
    this._viewsService = viewsService;
  }

  public saveState(): Promise<ICaseState> {
    const views = this._viewsService.views;
    const dataToSave: StateMetadata = [];

    views.forEach((view) => {
      const viewDataToSave: viewData = {
        viewDescriptor: view.getDescriptor(),
        measurements: []
      };

      const addedMeasurements = view.getMeasurements().filter((m) => {
        return m.kind === SceneItemKind.DiameterMeasurement && !m.imported;
      });
      addedMeasurements?.forEach((m) => {
        viewDataToSave.measurements.push({
          descriptor: m.descriptor,
          label: m.label,
          kind: m.kind,
          phaseId: m.phaseId,
          syncSliceDescriptor: m.syncSliceDescriptor,
          imagesMode: m.imagesMode,
          zoomFactor: m.zoomFactor,
          attachments: []
        });
      });
      dataToSave.push(viewDataToSave);
    });

    const state: ICaseState<StateMetadata> = {
      version: '1.1',
      metadata: dataToSave
    };

    return Promise.resolve(state);
  }

  public loadState(metadata: unknown): Promise<void> {
    const viewsIndicationsData = metadata as StateMetadata;

    viewsIndicationsData.forEach((viewData) => {
      const view = this._viewsService.getViewByDescriptor(viewData.viewDescriptor);
      if (!view) return;

      // TODO: [chebotarev] I think the complete data model has to be serialized.
      // Overriding of half of the fields with defaults is quite risky.
      viewData.measurements.forEach((measurementsData) => {
        const viewModel = new MeasurementViewModel({
          descriptor: measurementsData.descriptor,
          attachments: [],
          label: measurementsData.label,
          details: undefined,
          syncSliceDescriptor: measurementsData.syncSliceDescriptor,
          color: defaultMeasurementModelParams.color,
          highlightColor: defaultMeasurementModelParams.highlightColor,
          selectionColor: defaultMeasurementModelParams.selectionColor,
          transparency: defaultMeasurementModelParams.transparency,
          isVisible: defaultMeasurementModelParams.isVisible,
          isContourVisible: defaultMeasurementModelParams.isContourVisible,
          isHighlighted: defaultMeasurementModelParams.isHighlighted,
          isSelected: defaultMeasurementModelParams.isSelected,
          isPickable: defaultMeasurementModelParams.isPickable,
          isTunable: false,
          isBeingEdited: false,
          imagesMode: measurementsData.imagesMode,
          kind: measurementsData.kind,
          imported: false,
          isAvailable: true,
          readyForSliding: false,
          isClippable: true,
          phaseId: measurementsData.descriptor,
          zoomFactor: measurementsData.zoomFactor
        });
        const measurementBoxes = view.getMeasurementBoxes();
        const diametersBox = measurementBoxes?.find((box) => box.kind === MeasurementKind.AreaDiameter);
        if (diametersBox !== undefined) {
          runInAction(() => {
            diametersBox.measurements.push(viewModel);
          });
        }
        view.addMeasurement(viewModel);
      });
    });
    return Promise.resolve();
  }
}
